// Categorical palette for user-supplied points: Kate Morley's 12-bit rainbow
// (https://iamkate.com/data/12-bit-rainbow/), given here as 6-digit hex so the values survive
// helpers that slice a hex string. Deliberately NOT in the published rainbow order — that
// puts neighbouring hues in consecutive slots, which is right for a sequence but not for
// telling points apart. Reordered for maximum separation, the first five slots clear both
// separation floors on a white plot surface (worst CVD deltaE 9.1, normal-vision 17.7);
// past five, points lean on their labels and the legend to carry identity. The lighter
// slots sit below 3:1 contrast against white, which the point labels likewise cover.
export const USER_DATA_COLOR_PALETTE = [
    '#881177', // 12-bit #817, purple
    '#eedd00', // 12-bit #ed0, yellow
    '#0099cc', // 12-bit #09c, blue
    '#cc6666', // 12-bit #c66, red
    '#44dd88', // 12-bit #4d8, green
    '#3366bb', // 12-bit #36b, indigo
    '#ee9944', // 12-bit #e94, orange
    '#aa3355', // 12-bit #a35, maroon
    '#00bbcc', // 12-bit #0bc, cyan
    '#663399', // 12-bit #639, violet
    '#99dd55', // 12-bit #9d5, lime
    '#22ccbb', // 12-bit #2cb, teal
];

// The colour(s) the API hands to a benchmark point that has never been styled. A point
// carrying one of these is unstyled and takes a palette colour; anything else is the user's
// own choice and is left alone. Taken from crossPlotPointOptions[].stringColor on unstyled
// points in /BenchmarkPlot/{id}/full. (#000080 is the globalData series colour, not this.)
export const BENCHMARK_DEFAULT_POINT_COLORS: string[] = ['#0000FF'];

const normaliseColor = (color?: string | null) => typeof color === 'string' ? color.trim().toLowerCase() : '';

// `colorIndex` is a series-level array running parallel to ids/markers/crossPlotPointOptions —
// the slot the API has reserved for each record in this workspace, stable across loads. Prefer
// it to the point's position in the response: position shifts whenever a record is added,
// removed, deselected or filtered out, and every colour after it moves with it. Empty on
// globalData series, which are styled series-wide rather than per point.
// Returns null when the payload doesn't carry one, so callers can fall back to position.
const getPointColorIndex = (series: { colorIndex?: (number | null)[] } | null | undefined, index: number): number | null => {
    const slot = series?.colorIndex?.[index];
    return typeof slot === 'number' && Number.isFinite(slot) ? slot : null;
};

// Benchmark user points arrive from the API sharing a single default colour, which makes
// every input look the same on the plot. Spread them across the palette so each input is
// identifiable, keeping any point the user has explicitly restyled on its own colour.
// Returns one colour array per userData series, aligned with that series' point order.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getBenchmarkUserDataColors = (userData: any[] | null | undefined): string[][] => {
    if (!Array.isArray(userData)) return [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const savedColors = userData.map((series: any) => {
        const length = series?.ids?.length ?? series?.x?.length ?? 0;
        return Array.from({ length }, (_, i) =>
            series?.crossPlotPointOptions?.[i]?.stringColor || series?.markers?.[i]?.color || '');
    });

    const knownDefaults = new Set(BENCHMARK_DEFAULT_POINT_COLORS.map(normaliseColor).filter(Boolean));
    let isDefaultColor = (key: string) => knownDefaults.has(key);

    if (!knownDefaults.size) {
        // Fallback while BENCHMARK_DEFAULT_POINT_COLORS is unset: assume the colour shared by
        // the most points is the API default, and that a colour used by a single point is a
        // deliberate choice. This cannot tell an unstyled set apart from one the user has
        // deliberately painted a single colour via the style panel's "all colour" option, so
        // that choice gets overridden until the default colour above is filled in.
        const counts = new Map<string, number>();
        savedColors.flat().forEach(color => {
            const key = normaliseColor(color);
            if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
        });
        let defaultColor = '';
        let highestCount = 1;
        counts.forEach((count, color) => {
            if (count > highestCount) {
                highestCount = count;
                defaultColor = color;
            }
        });
        isDefaultColor = (key: string) => key === defaultColor;
    }

    let pointIndex = 0;
    return savedColors.map((series, seriesIndex) => series.map((color, index) => {
        const slot = getPointColorIndex(userData[seriesIndex], index) ?? pointIndex;
        const paletteColor = USER_DATA_COLOR_PALETTE[slot % USER_DATA_COLOR_PALETTE.length];
        pointIndex += 1;
        const key = normaliseColor(color);
        return !key || isDefaultColor(key) ? paletteColor : color;
    }));
};

export const checkDarkOrLigth = (color: string) => {
    if(!color) return 'dark'
    const c = color.substring(1); // strip #
    const rgb = parseInt(c, 16); // convert rrggbb to decimal
    const r = (rgb >> 16) & 0xff; // extract red
    const g = (rgb >> 8) & 0xff; // extract green
    const b = (rgb >> 0) & 0xff; // extract blue
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709
    return luma < 160 ? 'light' : 'dark';
}